import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { qcApi as Q, QC_STATUS_STYLE, GRADE_STYLE } from "@/lib/qc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Loader2, Inbox as InboxIcon, PackageCheck } from "lucide-react";

export default function QualityControl() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [] });
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("queue");
  const [inbox, setInbox] = useState([]);
  const [rows, setRows] = useState([]);
  const [passed, setPassed] = useState([]);
  const [rework, setRework] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const canCreate = can("quality_control", "create");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, ib, list, ps, rw] = await Promise.all([Q.stats(), Q.inbox(), Q.inspections({ search: search || undefined, status }), Q.passed(), Q.rework()]);
      setStats(s); setInbox(ib); setRows(list); setPassed(ps); setRework(rw);
    } finally { setLoading(false); }
  };
  useEffect(() => { Q.meta().then(setMeta); }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(loadAll, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const receiveAll = async () => {
    try { const r = await Q.receive(null); toast.success(`Received ${r.received} finished item(s)`); await loadAll(); setTab("queue"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const receiveOne = async (batchId) => {
    try { await Q.receive([batchId]); toast.success("Received for inspection"); await loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const InspectionTable = ({ data, emptyMsg }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead className="text-xs uppercase text-slate-500">Inspection</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Roll / Package</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Meters</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Grade</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/quality-control/inspections/${r.id}`)} data-testid={`inspection-row-${r.inspection_no}`}>
              <TableCell className="font-medium text-slate-800">{r.inspection_no}<span className="block text-xs text-slate-400">{r.job_no}</span></TableCell>
              <TableCell className="text-slate-700">{r.batch_id}</TableCell>
              <TableCell className="text-slate-700">{r.customer}</TableCell>
              <TableCell className="tabular-nums text-slate-700">{r.packed_meters} m</TableCell>
              <TableCell>{r.grade ? <Badge className={`hover:bg-inherit ${GRADE_STYLE[r.grade]}`}>{r.grade}</Badge> : <span className="text-slate-400">—</span>}</TableCell>
              <TableCell><Badge className={`hover:bg-inherit ${QC_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
            </TableRow>
          ))}
          {data.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400">{emptyMsg}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="qc-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Quality Control</h2>
        <p className="mt-1 text-sm text-slate-500">Inspect finished production output. Only passed rolls proceed to Inventory; rejects go to rework.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="qc-stat-inbox"><p className="text-xs uppercase tracking-wider text-slate-500">Inbox</p><p className="mt-1 font-heading text-2xl font-bold text-amber-600">{stats.inbox ?? 0}</p></Card>
        <Card className="p-4" data-testid="qc-stat-pending"><p className="text-xs uppercase tracking-wider text-slate-500">In inspection</p><p className="mt-1 font-heading text-2xl font-bold text-blue-600">{stats.pending ?? 0}</p></Card>
        <Card className="p-4" data-testid="qc-stat-passrate"><p className="text-xs uppercase tracking-wider text-slate-500">Pass rate</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600">{stats.pass_rate ?? 0}%</p></Card>
        <Card className="p-4" data-testid="qc-stat-meters"><p className="text-xs uppercase tracking-wider text-slate-500">Passed meters</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{(stats.passed_meters ?? 0).toLocaleString("en-IN")}</p></Card>
      </div>

      <Card className="p-4">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList data-testid="qc-tabs">
                <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox {inbox.length ? `(${inbox.length})` : ""}</TabsTrigger>
                <TabsTrigger value="queue" data-testid="tab-queue">Inspection Queue</TabsTrigger>
                <TabsTrigger value="passed" data-testid="tab-passed">Passed → Inventory</TabsTrigger>
                <TabsTrigger value="rework" data-testid="tab-rework">Rework</TabsTrigger>
              </TabsList>
              {tab === "queue" && (
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="w-52 pl-9" data-testid="qc-search-input" />
                  </div>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-48" data-testid="qc-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <TabsContent value="inbox" className="mt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-slate-500">Finished production output waiting to be received into QC.</p>
                {canCreate && inbox.length > 0 && <Button onClick={receiveAll} data-testid="receive-all-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><InboxIcon className="mr-2 h-4 w-4" />Receive all</Button>}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-500">Roll / Package</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Job</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Meters</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Prod grade</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {inbox.map((f) => (
                      <TableRow key={f.batch_id} data-testid={`inbox-row-${f.batch_id}`}>
                        <TableCell className="font-medium text-slate-800">{f.batch_id}</TableCell>
                        <TableCell className="text-slate-700">{f.job_no}</TableCell>
                        <TableCell className="text-slate-700">{f.customer}</TableCell>
                        <TableCell className="tabular-nums text-slate-700">{f.meters} m</TableCell>
                        <TableCell>{f.grade ? <Badge className={`hover:bg-inherit ${GRADE_STYLE[f.grade]}`}>{f.grade}</Badge> : "—"}</TableCell>
                        <TableCell className="text-right">{canCreate && <Button size="sm" variant="outline" onClick={() => receiveOne(f.batch_id)} data-testid={`receive-${f.batch_id}`}>Receive</Button>}</TableCell>
                      </TableRow>
                    ))}
                    {inbox.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="inbox-empty">No finished output awaiting QC.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            <TabsContent value="queue" className="mt-4"><InspectionTable data={rows} emptyMsg="No inspections yet. Receive finished output from the Inbox." /></TabsContent>
            <TabsContent value="passed" className="mt-4">
              <p className="mb-3 flex items-center gap-2 text-sm text-emerald-600"><PackageCheck className="h-4 w-4" />These verified rolls are published to Inventory.</p>
              <InspectionTable data={passed} emptyMsg="No passed rolls yet." />
            </TabsContent>
            <TabsContent value="rework" className="mt-4"><InspectionTable data={rework} emptyMsg="No items awaiting rework." /></TabsContent>
          </Tabs>
        )}
      </Card>
    </div>
  );
}
